import { useState, useMemo } from 'react';
import ImageCard from './ImageCard';
import ImageLightbox, { type LightboxImage } from './ImageLightbox';
import { getFilenameFromUrl } from '@/lib/imageUtils';
import { unique } from '@/lib/utils';

interface ImageGalleryProps {
  images: string[];
  title: string;
  compact?: boolean;
  naturalLoading?: boolean;
}

export default function ImageGallery({ images, title, compact = false, naturalLoading = false }: ImageGalleryProps) {
  const [lightboxState, setLightboxState] = useState<{ images: LightboxImage[]; index: number } | null>(null);
  // De-dupe so identical URLs don't render twice or collide as React keys (key={url} below).
  const uniqueImages = useMemo(() => unique(images), [images]);
  const lightboxImages = useMemo<LightboxImage[]>(
    () => uniqueImages.map(url => ({ url, filename: getFilenameFromUrl(url) })),
    [uniqueImages]
  );

  if (uniqueImages.length === 0) {
    return null;
  }

  return (
    <>
      <div className="glass-card rounded-lg p-6 space-y-4">
        {/* Title */}
        {title && <h2 className="text-xl font-semibold">{title}</h2>}

        {/* Image Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {uniqueImages.map((url, i) => (
            <ImageCard
              key={url}
              url={url}
              compact={compact}
              naturalLoading={naturalLoading}
              onOpenLightbox={() => setLightboxState({ images: lightboxImages, index: i })}
            />
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxState && (
        <ImageLightbox
          images={lightboxState.images}
          currentIndex={lightboxState.index}
          isOpen={!!lightboxState}
          onClose={() => setLightboxState(null)}
          onNavigate={(index) => setLightboxState(prev => prev ? { ...prev, index } : null)}
        />
      )}
    </>
  );
}
